import { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Switch, InputNumber, Typography, notification } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import ajaxService from "../../../services/ajax-service";

const { Title, Text } = Typography;

const Features = () => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const res = await ajaxService.get("/features");
      if (res.success) {
        setDataSource(res.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  const openAddModal = () => {
    setEditingFeature(null);
    form.resetFields();
    setShowModal(true);
  };

  const openEditModal = (record) => {
    setEditingFeature(record);
    form.setFieldsValue({
      name: record.name,
      display_name: record.display_name,
      unlock_from_video: Boolean(record.unlock_from_video),
      unlock_from_bundle: Boolean(record.unlock_from_bundle),
      quantity_expiry_duration: record.quantity_expiry_duration,
      video_expiry_duration: record.video_expiry_duration,
      quantity_paid_user: record.quantity_paid_user,
      quantity_from_video: record.quantity_from_video,
      quantity_free_user: record.quantity_free_user,
    });
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingFeature(null);
    form.resetFields();
  };

  const onFinish = async (values) => {
    try {
      let res;
      if (editingFeature) {
        res = await ajaxService.put(`/features/`,editingFeature.id, values);
      } else {
        res = await ajaxService.post("/features", values);
      }

      if (res.success) {
        notification.open({
          message: editingFeature ? "Feature updated successfully" : "Feature added successfully",
          icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
        });
        handleCancel();
        fetchFeatures();
      } else {
        throw new Error();
      }
    } catch (e) {
      notification.open({
        message: "Unable to save feature",
        icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await ajaxService.delete(`/features/`,deleteId);
      if (res.success) {
        notification.success({ message: "Feature deleted successfully" });
        fetchFeatures();
      }
    } finally {
      setDeleteId(null);
    }
  };

  const columns = [
    {
      title: "SNo",
      key: "sno",
      width: 100,
      fixed: "left",
      render: (_, __, index) => <Text>{index + 1}</Text>,
    },
    { title: "Display Name", dataIndex: "display_name", key: "display_name" },
    { title: "Qty Expiry (days)", dataIndex: "quantity_expiry_duration", key: "quantity_expiry_duration" },
    { title: "Paid Qty", dataIndex: "quantity_paid_user", key: "quantity_paid_user" },
    { title: "Video Qty", dataIndex: "quantity_from_video", key: "quantity_from_video" },
    { title: "Free Qty", dataIndex: "quantity_free_user", key: "quantity_free_user" },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 140,
    //   render: (_, record) => (
    //     // <div style={{ display: "flex", gap: 8 }}>
    //     //   <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
    //     //   <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setDeleteId(record.id)} />
    //     // </div>
    //   ),
    // },
  ];

  return (
    <div className="page-container">
      <div className="module-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 className="module-title">Feature Management</h1>
          <p className="module-subtitle">Manage application features</p>
        </div>
        {/* <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
          Add Feature
        </Button> */}
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: 1200 }}
      />

      {/* Add / Edit Modal */}
      <Modal open={showModal} onCancel={handleCancel} footer={null} centered destroyOnClose width={700}>
        <Title level={4} style={{ textAlign: "center" }}>
          {editingFeature ? "Edit Feature" : "Add Feature"}
        </Title>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            unlock_from_video: true,
            unlock_from_bundle: false,
          }}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input placeholder="Internal name" />
          </Form.Item>

          <Form.Item name="display_name" label="Display Name">
            <Input placeholder="Display name" />
          </Form.Item>

          <Form.Item name="unlock_from_video" label="Unlock From Video" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="unlock_from_bundle" label="Unlock From Bundle" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="quantity_expiry_duration" label="Quantity Expiry Duration (days)">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="video_expiry_duration" label="Video Expiry Duration (days)">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="quantity_paid_user" label="Quantity for Paid User">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="quantity_from_video" label="Quantity From Video">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="quantity_free_user" label="Quantity for Free User">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Button style={{ marginRight: 8 }} onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editingFeature ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>Are you sure you want to delete this feature?</p>
      </Modal>
    </div>
  );
};

export default Features;